import { createDrumVoice, type DrumVoice } from './drums'
import { createFxBus, type FxBus } from './fxBus'
import { createMonoSine, createPolySine, type TrackVoice } from './synths'
import { DRUMS, type DrumId } from '../constants/drums'
import type { KeyboardTrackId, TrackId } from '../constants/scale'

export interface AudioGraph {
  drumVoices: Record<DrumId, DrumVoice>
  synthVoices: Record<KeyboardTrackId, TrackVoice>
  buses: Record<TrackId, FxBus>
  ready: Promise<void>
  dispose(): void
}

export function buildAudioGraph(): AudioGraph {
  const buses: Record<TrackId, FxBus> = {
    drums: createFxBus(),
    keys: createFxBus(),
    bass: createFxBus(),
    lead: createFxBus(),
  }

  const drumVoices = {} as Record<DrumId, DrumVoice>
  for (const { id } of DRUMS) {
    const voice = createDrumVoice(id)
    voice.connect(buses.drums.input)
    drumVoices[id] = voice
  }

  const keys = createPolySine()
  keys.connect(buses.keys.input)
  const bass = createMonoSine()
  bass.connect(buses.bass.input)
  const lead = createMonoSine()
  lead.connect(buses.lead.input)
  const synthVoices: Record<KeyboardTrackId, TrackVoice> = { keys, bass, lead }

  const ready = Promise.all(Object.values(buses).map((b) => b.ready)).then(
    () => undefined,
  )

  return {
    drumVoices,
    synthVoices,
    buses,
    ready,
    dispose() {
      Object.values(drumVoices).forEach((v) => v.dispose())
      Object.values(synthVoices).forEach((v) => v.dispose())
      Object.values(buses).forEach((b) => b.dispose())
    },
  }
}
